import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { forwardRef, useState, useRef, useContext, useEffect } from 'react';
import classNames from 'classnames';
import _jsx from '@babel/runtime/helpers/jsx';
import { useFocusRing, FocusRing } from '@react-aria/focus';
import { getPatchedI18n, AndesContext, useId } from '@andes/context';
import { BACKSPACE, TAB, KEY_V } from '@andes/common/build/constants/keyboardCodes';
import { autofocus } from '@andes/common/build/utils/autofocus';
import { isWebView } from '@andes/common/build/webkit/utils';
import { hasTouchSupport } from '@andes/common/build/utils/isMobile';
import includes from '@andes/common/build/utils/includes';
import FeedbackNegative12 from '@andes/icons/FeedbackNegative12';
import useSrLabel from '@andes/common/build/hooks/useSrLabel';
import { VisuallyHidden } from '@andes/common';
import warning from '@andes/common/build/utils/warning';
import 'prop-types';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import VisibilityON20 from '@andes/icons/VisibilityON20';
import VisibilityOFF20 from '@andes/icons/VisibilityOFF20';
import transformToKebabCase from '@andes/common/build/utils/transformToKebabCase';
import { DropdownStandalone, DropdownItem } from '@andes/dropdown';
import ProgressIndicatorCircular from '@andes/progress-indicator-circular';
import autosize from 'autosize';
var module$2 = "@andes/textfield";
var locale$2 = "en-US";
var translations$2 = {
  "andes-textfield\u0004{0} caracteres restantes": [null, "{0} remaining characters"],
  "andes-textfield\u0004Maximo {0} caracteres": [null, "Maximum of {0} characters"],
  "andes-textfield\u0004Campo de texto valido": [null, "Valid text field"],
  "andes-textfield\u0004Campo de texto procesando": [null, "Text field processing"],
  "andes-textfield\u0004Borrar": [null, "Remove"],
  "andes-textfield\u0004Dígito {0}": [null, "Digit {0}"],
  "andes-textfield\u0004Mostrar contraseña": [null, "Show Password"]
};
var enTranslations = {
  module: module$2,
  locale: locale$2,
  translations: translations$2
};
var module$1 = "@andes/textfield";
var locale$1 = "es-AR";
var translations$1 = {
  "andes-textfield\u0004{0} caracteres restantes": [null, "{0} caracteres restantes"],
  "andes-textfield\u0004Maximo {0} caracteres": [null, "Máximo {0} caracteres"],
  "andes-textfield\u0004Campo de texto valido": [null, "Campo de texto valido"],
  "andes-textfield\u0004Campo de texto procesando": [null, "Campo de texto procesando"],
  "andes-textfield\u0004Borrar": [null, "Borrar"],
  "andes-textfield\u0004Dígito {0}": [null, "Dígito {0}"],
  "andes-textfield\u0004Mostrar contraseña": [null, "Mostrar Contraseña"]
};
var esTranslations = {
  module: module$1,
  locale: locale$1,
  translations: translations$1
};
var module = "@andes/textfield";
var locale = "pt-BR";
var translations = {
  "andes-textfield\u0004{0} caracteres restantes": [null, "{0} caracteres restantes"],
  "andes-textfield\u0004Maximo {0} caracteres": [null, "Máximo de {0} caracteres"],
  "andes-textfield\u0004Campo de texto valido": [null, "Campo de texto válido"],
  "andes-textfield\u0004Campo de texto procesando": [null, "Processamento de campo de texto"],
  "andes-textfield\u0004Borrar": [null, "Excluir"],
  "andes-textfield\u0004Dígito {0}": [null, "Dígito {0}"],
  "andes-textfield\u0004Mostrar contraseña": [null, "Mostrar Senha"]
};
var ptTranslations = {
  module: module,
  locale: locale,
  translations: translations
};
var _FeedbackNegative;
const ErrorIcon = _ref => {
  let {
    namespace
  } = _ref;
  return /*#__PURE__*/_jsx("span", {
    className: "".concat(namespace, "__error-icon"),
    "aria-hidden": true
  }, void 0, _FeedbackNegative || (_FeedbackNegative = /*#__PURE__*/_jsx(FeedbackNegative12, {
    color: "white"
  })));
};
var ErrorIcon$1 = ErrorIcon;
var _path;
const ValidatedIcon = _ref => {
  let {
    namespace,
    notificationsLabel
  } = _ref;
  const srLabelVisibility = useSrLabel();
  return /*#__PURE__*/_jsx("span", {
    role: "status",
    "aria-live": "polite"
  }, void 0, /*#__PURE__*/_jsx(VisuallyHidden, {}, void 0, srLabelVisibility && notificationsLabel), /*#__PURE__*/_jsx("svg", {
    className: "".concat(namespace, "__validated-icon"),
    "aria-hidden": true,
    width: "24",
    height: "18",
    viewBox: "0 0 24 24"
  }, void 0, _path || (_path = /*#__PURE__*/_jsx("path", {
    fill: "#00A650",
    d: "M10.182 14.094l6.865-6.865L18.59 8.77l-8.408 8.408-4.772-4.771 1.543-1.543z"
  }))));
};
var ValidatedIcon$1 = ValidatedIcon;
const computeHelperProps = _ref => {
  let {
    message,
    messageAction,
    messageActionOnClick,
    messageShow,
    helper: helperProp
  } = _ref;
  const helperConfig = {
    helper: message || helperProp,
    action: messageAction,
    onActionClick: messageActionOnClick,
    show: messageShow !== false
  };
  return helperConfig;
};
var computeHelperProps$1 = computeHelperProps;
const WARNING_NUMERIC = '<Numeric> component will be deprecated in the next major release. Use <CodeInput> instead.';
var _ErrorIcon$3;
const numericNamespace = 'andes-form-control--numeric';
const namespace$3 = 'andes-form-control';
const translationsNamespace = 'andes-textfield';
const onlyNumberRegex = /^[0-9]*$/;
const DEFAULT_FOCUS_INDEX = 0;
const NOT_FOCUSABLE_INDEX = -1;
const numbersRegex = /[^0-9]/gi;
const specialCharacters = ['Backspace', 'Tab', 'Enter', 'Control', 'Shift', 'Escape', 'Meta', 'Alt', 'CapsLock', 'ArrowLeft', 'ArrowRight'];
const getFocusIndexOrFalsy = focus => focus === false ? NOT_FOCUSABLE_INDEX : Number(focus === true ? DEFAULT_FOCUS_INDEX : focus);
const getDefaultValue = digits => {
  if (!digits || typeof digits !== 'number') return [];
  return new Array(digits).fill('');
};
const isEmpty = value => {
  const array = typeof value === 'string' ? value.split('') : value;
  return array.every(item => !item);
};
const parsedValues = (value, digits) => {
  if (typeof value === 'string') {
    let parsedValue = value.replace(numbersRegex, '').split('');
    if (digits && parsedValue.length < digits) {
      const diff = digits - parsedValue.length;
      const arrayEmpty = new Array(diff).fill('');
      parsedValue = parsedValue.concat(arrayEmpty);
    }
    return parsedValue;
  }
  if (Array.isArray(value)) return value;
};
const Numeric = /*#__PURE__*/function () {
  const Numeric = /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
      centered = false,
      className,
      defaultValue,
      digits = 1,
      disabled = false,
      deprecated = true,
      focus = DEFAULT_FOCUS_INDEX,
      helper,
      id,
      label,
      message: willdepreacate$message,
      messageShow: willdeprecate$messageShow,
      modifier,
      onChange,
      onPaste,
      srLabel,
      strict = false,
      value
    } = _ref;
    const helperConfig = computeHelperProps$1({
      message: willdepreacate$message,
      messageShow: willdeprecate$messageShow,
      helper
    });
    React.useEffect(() => {
      warning(!deprecated, WARNING_NUMERIC);
    }, []);
    const [inputValue, setInputValue] = useState(parsedValues(defaultValue, digits) || getDefaultValue(digits));
    const [focusedItem, setFocusedItem] = useState(getFocusIndexOrFalsy(focus));
    const {
      current: isControlled
    } = useRef(typeof value !== 'undefined');
    const currentValue = isControlled ? parsedValues(value, digits) || [] : inputValue;
    const inputsRef = useRef([]);
    const firstRender = useRef(true);
    const {
      isFocusVisible,
      focusProps
    } = useFocusRing();
    const i18n = getPatchedI18n(AndesContext, enTranslations, esTranslations, ptTranslations);
    const {
      device
    } = useContext(AndesContext);
    const isMobile = device === null || device === void 0 ? void 0 : device.mobile;
    const componentId = useId(id);
    const moveFocusBackwards = (event, index) => {
      const newIndex = index - 1;
      const newValue = [...currentValue];
      if (newIndex >= 0) newValue[newIndex] = '';
      setFocusedItem(state => newIndex > -1 ? newIndex : state);
      if (!isControlled) {
        setInputValue(newValue);
      }
      if (typeof onChange === 'function') {
        onChange(event, newValue);
      }
    };
    const moveFocusForward = () => {
      setFocusedItem(state => {
        const newIndex = state + 1;
        return newIndex < digits ? newIndex : state;
      });
    };
    const assignPastedValues = (event, values) => {
      const newValues = currentValue;
      const newFocus = values.length > digits ? digits - 1 : values.length - 1;
      for (let i = 0; i < currentValue.length; i += 1) {
        var _values$i;
        newValues[i] = (_values$i = values[i]) !== null && _values$i !== void 0 ? _values$i : '';
      }
      setInputValue(newValues);
      setFocusedItem(newFocus);
      if (typeof onPaste === 'function') onPaste(event, newValues);
    };
    const handlePaste = event => {
      var _event$clipboardData;
      const textPlainItems = Array.from((event === null || event === void 0 || (_event$clipboardData = event.clipboardData) === null || _event$clipboardData === void 0 ? void 0 : _event$clipboardData.items) || []).filter(item => item.type === 'text/plain' || item.type === 'text');
      if (textPlainItems.length > 0) {
        var _textPlainItems$;
        (_textPlainItems$ = textPlainItems[0]) === null || _textPlainItems$ === void 0 || _textPlainItems$.getAsString(text => {
          const pastedValues = text.replace(numbersRegex, '').split('');
          assignPastedValues(event, pastedValues);
        });
      }
    };
    const onInputHandler = event => {
      const {
        target: {
          value: eventValue
        }
      } = event;
      if ((eventValue === null || eventValue === void 0 ? void 0 : eventValue.length) === digits) {
        setFocusedItem(digits - 1);
        return;
      }
      if (!onlyNumberRegex.test(eventValue) || eventValue.length > 1 && eventValue.length !== digits) {
        return;
      }
      if ((eventValue === null || eventValue === void 0 ? void 0 : eventValue.trim()) !== '') {
        moveFocusForward();
      }
    };
    const onChangeHandler = (event, index) => {
      const {
        target: {
          value: eventValue
        }
      } = event;
      if (!onlyNumberRegex.test(eventValue) || eventValue.length > 1 && eventValue.length !== digits) {
        return;
      }
      const newValue = [...currentValue];
      if (eventValue.length === digits) {
        const values = eventValue.split('');
        for (let i = 0; i < currentValue.length; i += 1) {
          var _values$i2;
          newValue[i] = (_values$i2 = values[i]) !== null && _values$i2 !== void 0 ? _values$i2 : '';
        }
      } else {
        newValue[index] = eventValue;
      }
      if (!isControlled) {
        setInputValue(newValue);
      }
      if (typeof onChange === 'function') {
        onChange(event, newValue);
      }
      return newValue;
    };
    const onKeyDownHandler = (event, index) => {
      const eventValue = event.target.value;
      if (event.code === BACKSPACE || event.key === 'Backspace') {
        if (eventValue === '') {
          moveFocusBackwards(event, index);
          return;
        }
      }
      if (event.code === TAB || event.key === 'Tab') {
        if (index === digits - 1 || index === 0) {
          setFocusedItem(NOT_FOCUSABLE_INDEX);
          return;
        }
      }
      const isPasting = (event.metaKey || event.ctrlKey) && event.code === KEY_V;
      if (isPasting) return;
      if (includes(specialCharacters, event.key)) return;
      const isInvalid = event.key.match(numbersRegex);
      if (isInvalid) {
        event.preventDefault();
      }
    };
    const focusValidItem = index => {
      const item = inputsRef.current[index];
      if (item) {
        if (!firstRender.current) {
          item.focus();
        } else {
          autofocus(item, firstRender.current);
          firstRender.current = false;
        }
      }
    };
    const onClickHandler = event => {
      if (isEmpty(currentValue) && (isWebView() || !hasTouchSupport() && !isMobile)) {
        event.preventDefault();
        if (focusedItem === DEFAULT_FOCUS_INDEX) {
          focusValidItem(DEFAULT_FOCUS_INDEX);
        } else {
          setFocusedItem(DEFAULT_FOCUS_INDEX);
        }
      }
    };
    const onFocusHandler = (event, index) => {
      setFocusedItem(index);
      if (typeof focusProps.onFocus === 'function') focusProps.onFocus(event);
    };
    const handleBlur = event => {
      setFocusedItem(NOT_FOCUSABLE_INDEX);
      if (typeof focusProps.onBlur === 'function') focusProps.onBlur(event);
    };
    useEffect(() => {
      if (focusedItem !== NOT_FOCUSABLE_INDEX) {
        focusValidItem(focusedItem);
      }
    }, [focusedItem]);
    const containerClassNames = classNames(numericNamespace, className, {
      ["".concat(namespace$3, "--").concat(modifier)]: modifier,
      ["".concat(namespace$3, "--centered")]: centered
    });
    return /*#__PURE__*/React.createElement("div", {
      className: containerClassNames,
      role: "group",
      "aria-label": srLabel,
      "aria-labelledby": label ? "".concat(componentId, "-label") : undefined,
      "aria-describedby": helperConfig.helper ? "".concat(componentId, "-message") : undefined,
      onPaste: handlePaste,
      ref: ref
    }, label && /*#__PURE__*/_jsx("span", {
      id: "".concat(componentId, "-label"),
      className: "".concat(namespace$3, "__label")
    }, void 0, label), /*#__PURE__*/_jsx("div", {
      className: "".concat(numericNamespace, "-wrapper")
    }, void 0, [...Array(digits)].map((_, index) => {
      const textFieldPadding = classNames("".concat(namespace$3, "__textfield-space"), {
        ["".concat(namespace$3, "__textfield-space--big")]: digits === 6 && index === 2
      });
      const inputWrapperClasses = classNames(namespace$3, "".concat(namespace$3, "--textfield"), {
        ["".concat(namespace$3, "--centered")]: centered,
        ["".concat(namespace$3, "--focused")]: focusedItem === index,
        ["".concat(namespace$3, "--visible-focused")]: isFocusVisible && focusedItem === index,
        ["".concat(namespace$3, "--").concat(modifier)]: modifier,
        ["".concat(namespace$3, "--disabled")]: disabled
      });
      return /*#__PURE__*/_jsx("div", {
        className: textFieldPadding
      }, "".concat(componentId, "-").concat(index), /*#__PURE__*/_jsx("div", {
        className: inputWrapperClasses
      }, void 0, /*#__PURE__*/_jsx("div", {
        className: "".concat(namespace$3, "__control")
      }, void 0, /*#__PURE__*/React.createElement("input", _extends({
        className: "".concat(namespace$3, "__field"),
        "aria-label": i18n.pgettext(translationsNamespace, 'Dígito {0}', index + 1),
        type: strict ? 'tel' : 'number',
        pattern: "[0-9]*",
        value: currentValue[index],
        id: "".concat(componentId, "-digit-").concat(index),
        onChange: evt => onChangeHandler(evt, index),
        onInput: onInputHandler,
        onKeyDown: evt => onKeyDownHandler(evt, index),
        onFocus: evt => onFocusHandler(evt, index),
        onBlur: handleBlur,
        onMouseDown: onClickHandler,
        disabled: disabled,
        ref: el => {
          inputsRef.current[index] = el;
        },
        "aria-invalid": modifier === 'error'
      }, index === 0 && {
        autoComplete: 'one-time-code',
        autoFocus: focus !== false
      })), modifier === 'completed' && /*#__PURE__*/_jsx(ValidatedIcon$1, {
        namespace: namespace$3,
        notificationsLabel: i18n.pgettext(translationsNamespace, 'Campo de texto valido')
      }))));
    })), helperConfig.show && helperConfig.helper && /*#__PURE__*/_jsx("div", {
      className: "".concat(namespace$3, "__bottom")
    }, void 0, modifier === 'error' && (_ErrorIcon$3 || (_ErrorIcon$3 = /*#__PURE__*/_jsx(ErrorIcon$1, {
      namespace: namespace$3
    }))), /*#__PURE__*/_jsx("div", {
      id: "".concat(componentId, "-message"),
      className: "".concat(namespace$3, "__message")
    }, void 0, helperConfig.helper)));
  });
  Numeric.displayName = 'Numeric';
  return Numeric;
}();
var Numeric$1 = Numeric;
const _excluded$3 = ["className", "id"];
const namespace$2 = 'andes-code-input';
const CodeInput = /*#__PURE__*/function () {
  const CodeInput = /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
        className,
        id
      } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded$3);
    const codeInputClassNames = classNames(namespace$2, className);
    const componentId = useId(id);
    return /*#__PURE__*/React.createElement(Numeric$1, _extends({
      className: codeInputClassNames,
      deprecated: false,
      id: componentId,
      ref: ref
    }, otherProps));
  });
  CodeInput.displayName = 'CodeInput';
  return CodeInput;
}();
var CodeInput$1 = CodeInput;
var handlers = {
  onFocus: function onFocus(element) {
    element === null || element === void 0 || element.classList.add('andes-form-control--focused');
  },
  onBlur: function onBlur(element, value) {
    element === null || element === void 0 || element.classList.remove('andes-form-control--focused');
    if (value && value.length) {
      element === null || element === void 0 || element.classList.add('andes-form-control--floated');
    } else {
      element === null || element === void 0 || element.classList.remove('andes-form-control--floated');
    }
  },
  onChange: function onChange(value, inputHiddenRef) {
    if (inputHiddenRef) {
      inputHiddenRef.value = value;
    }
  }
};
const _excluded$2 = ["autoFocus", "className", "defaultValue", "helper", "hideLabel", "hidePasswordLabel", "id", "innerRef", "inputProps", "label", "maxLength", "message", "messageShow", "modifier", "onClickShowPassword", "show", "showHide", "showLabel", "showPasswordLabel", "srLabel", "value"];
var _VisibilityON, _VisibilityOFF, _ErrorIcon$2;
const namespace$1 = 'andes-form-control';
class Password extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      focused: props.autoFocus || false,
      showPassword: false
    };
    this.isUncontrolledValue = props.value === undefined;
    this.setField = this.setField.bind(this);
    this.setContainer = this.setContainer.bind(this);
    this.onBlurHandler = this.onBlurHandler.bind(this);
    this.onFocusHandler = this.onFocusHandler.bind(this);
    this.onChangeHandler = this.onChangeHandler.bind(this);
    this.onChangeIconHandler = this.onChangeIconHandler.bind(this);
    this.renderShowHidePasswordButton = this.renderShowHidePasswordButton.bind(this);
    this.setHiddenPass = this.setHiddenPass.bind(this);
  }
  get helperConfig() {
    const {
      message,
      messageShow,
      helper
    } = this.props;
    return computeHelperProps$1({
      message,
      messageShow,
      helper
    });
  }
  onBlurHandler(event) {
    const {
      container,
      field
    } = this;
    const {
      value
    } = field;
    const {
      onBlur
    } = this.props;
    handlers.onBlur(container, value);
    this.setState({
      focused: false
    });
    if (onBlur) onBlur(event);
  }
  onFocusHandler(event) {
    const {
      container
    } = this;
    const {
      onFocus
    } = this.props;
    handlers.onFocus(container);
    this.setState({
      focused: true
    });
    if (onFocus) onFocus(event);
  }
  onChangeHandler(event) {
    const {
      target: {
        value
      }
    } = event;
    const {
      showHide = false,
      onChange
    } = this.props;
    const {
      hiddenPass
    } = this;
    if (showHide && this.isUncontrolledValue) handlers.onChange(value, hiddenPass);
    if (onChange) onChange(event);
  }
  onChangeIconHandler() {
    const {
      onClickShowPassword
    } = this.props;
    this.setState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
      showPassword: !prevState.showPassword
    }));
    if (onClickShowPassword) onClickShowPassword();
  }
  setField(fieldRef) {
    this.field = fieldRef;
  }
  setContainer(containerRef) {
    const {
      innerRef
    } = this.props;
    this.container = containerRef;
    if (innerRef) innerRef.current = containerRef;
  }
  setHiddenPass(passRef) {
    this.hiddenPass = passRef;
  }
  renderShowHidePasswordButton(i18n) {
    const {
      id
    } = this.props;
    const {
      showPassword
    } = this.state;
    return /*#__PURE__*/_jsx("div", {
      className: "".concat(namespace$1, "__password-embedded")
    }, void 0, /*#__PURE__*/_jsx("input", {
      checked: showPassword,
      className: "".concat(namespace$1, "__password-checkbox"),
      id: "".concat(id, "-checkbox"),
      type: "checkbox",
      onChange: this.onChangeIconHandler,
      "aria-label": i18n.pgettext('andes-textfield', 'Mostrar contraseña')
    }), /*#__PURE__*/_jsx("div", {
      className: "".concat(namespace$1, "__password-icon")
    }, void 0, showPassword ? _VisibilityON || (_VisibilityON = /*#__PURE__*/_jsx(VisibilityON20, {})) : _VisibilityOFF || (_VisibilityOFF = /*#__PURE__*/_jsx(VisibilityOFF20, {}))));
  }
  render() {
    var _label, _span;
    const _this$props = this.props,
      {
        autoFocus = false,
        className,
        defaultValue,
        helper: helperProp,
        hideLabel: willdeprecate$hideLabel,
        hidePasswordLabel: willdeprecate$hidePasswordLabel,
        id,
        innerRef,
        inputProps = {},
        label,
        maxLength = 120,
        message: willdeprecate$message,
        messageShow: willdeprecate$messageShow,
        modifier,
        onClickShowPassword,
        show: willdeprecate$show,
        showHide = false,
        showLabel: willdeprecate$showLabel,
        showPasswordLabel: willdeprecate$showPasswordLabel,
        srLabel,
        value
      } = _this$props,
      other = _objectWithoutProperties(_this$props, _excluded$2);
    const {
      focused,
      showPassword
    } = this.state;
    const {
      helper,
      show: showHelper
    } = this.helperConfig;
    const fieldProps = _objectSpread(_objectSpread(_objectSpread({}, other), inputProps), {}, {
      'aria-describedby': helper ? "".concat(id, "-message") : undefined,
      'aria-invalid': modifier === 'error',
      'aria-label': srLabel,
      autoFocus,
      className: "".concat(namespace$1, "__field"),
      id,
      maxLength: maxLength,
      onBlur: this.onBlurHandler,
      onChange: this.onChangeHandler,
      onFocus: this.onFocusHandler,
      ref: this.setField,
      type: showPassword ? 'text' : 'password',
      value,
      defaultValue,
      autoComplete: inputProps.autoComplete || 'current-password'
    });
    const fieldHiddenProps = {
      style: {
        display: 'none'
      },
      type: 'password',
      id: 'password-hidden',
      value,
      defaultValue,
      ref: this.setHiddenPass,
      autoComplete: inputProps.autoComplete || 'current-password'
    };
    const classes = classNames(namespace$1, className, "".concat(namespace$1, "--textfield"), {
      ["".concat(namespace$1, "--password")]: showHide,
      ["".concat(namespace$1, "--focused")]: autoFocus,
      ["".concat(namespace$1, "--").concat(modifier)]: modifier
    });
    return /*#__PURE__*/_jsx(AndesContext.Consumer, {}, void 0, andesContext => {
      const i18n = getPatchedI18n(andesContext, enTranslations, esTranslations, ptTranslations);
      return /*#__PURE__*/React.createElement("div", {
        className: classes,
        ref: this.setContainer
      }, label && (_label || (_label = /*#__PURE__*/_jsx("label", {
        htmlFor: id,
        className: "".concat(namespace$1, "__label")
      }, void 0, label))), /*#__PURE__*/_jsx(FocusRing, {
        within: true,
        isTextInput: true,
        focusRingClass: focused ? 'visible-focused' : ''
      }, void 0, /*#__PURE__*/_jsx("div", {
        className: "".concat(namespace$1, "__control")
      }, void 0, /*#__PURE__*/React.createElement("input", _extends({
        key: "field"
      }, fieldProps)), /*#__PURE__*/React.createElement("input", _extends({
        key: "field-password-hidden"
      }, fieldHiddenProps)), showHide && this.renderShowHidePasswordButton(i18n))), showHelper && (helper || showHide) && /*#__PURE__*/_jsx("div", {
        className: "".concat(namespace$1, "__bottom")
      }, void 0, modifier === 'error' && (_ErrorIcon$2 || (_ErrorIcon$2 = /*#__PURE__*/_jsx(ErrorIcon$1, {
        namespace: namespace$1
      }))), /*#__PURE__*/_jsx("span", {
        className: "".concat(namespace$1, "__message"),
        id: "".concat(id, "-message")
      }, void 0, _span || (_span = /*#__PURE__*/_jsx("span", {
        className: "".concat(namespace$1, "__message-text")
      }, void 0, helper)))));
    });
  }
}
var Password$1 = Password;
const PasswordContainer = /*#__PURE__*/function () {
  const PasswordContainer = /*#__PURE__*/forwardRef((props, ref) => {
    const componentId = useId(props.id);
    return /*#__PURE__*/(
      React.createElement(Password$1, _extends({
        innerRef: ref
      }, props, {
        id: componentId
      }))
    );
  });
  PasswordContainer.displayName = 'PasswordContainer';
  return PasswordContainer;
}();
var PasswordContainer$1 = PasswordContainer;
var _svg;
const ClearButton = _ref => {
  let {
    namespace,
    onClick,
    'aria-label': ariaLabel = ''
  } = _ref;
  return /*#__PURE__*/_jsx("button", {
    "aria-label": ariaLabel,
    className: "".concat(namespace, "__clear-icon"),
    onClick: onClick,
    onMouseDown: e => e.preventDefault()
  }, void 0, _svg || (_svg = /*#__PURE__*/_jsx("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16"
  }, void 0, /*#__PURE__*/_jsx("path", {
    fill: "#000",
    fillOpacity: ".45",
    fillRule: "nonzero",
    d: "M3.2 2.347l4.802 4.802 4.795-4.793.848.848-4.794 4.794 4.794 4.794-.848.848-4.794-4.794L3.2 13.649l-.848-.848 4.802-4.803-4.802-4.803.848-.848z"
  }))));
};
var ClearButton$1 = ClearButton;
const formControlNamespace = 'andes-form-control';
const splitButtonNamespace = "".concat(formControlNamespace, "__split-button");
const namespace = 'andes-textfield';
const _excluded$1 = ["centered", "children", "className", "clear", "countdown", "disabled", "defaultValue", "embeddedChildren", "focus", "id", "innerRef", "inputProps", "label", "labelSrOnly", "maxLength", "message", "messageAction", "messageActionOnClick", "messageShow", "modifier", "multiline", "numeric", "onChange", "placeholder", "prefix", "rows", "select", "setField", "split", "srLabel", "suffix", "value", "autoFocus"];
var _ErrorIcon$1;
const emptyEvent = {
  target: {
    value: ''
  }
};
class TextField extends React.Component {
  constructor(props) {
    super(props);
    const value = String(props.defaultValue || props.value || '');
    this.state = {
      height: 'auto',
      countdown: value.length,
      focused: props.autoFocus || false
    };
    this.isUncontrolledValue = props.value === undefined;
    this.setField = this.setField.bind(this);
    this.setRef = this.setRef.bind(this);
    this.onBlurHandler = this.onBlurHandler.bind(this);
    this.onFocusHandler = this.onFocusHandler.bind(this);
    this.onInputHandler = this.onInputHandler.bind(this);
    this.onChangeHandler = this.onChangeHandler.bind(this);
    this.onClearHandler = this.onClearHandler.bind(this);
  }
  componentDidMount() {
    const {
      multiline = false,
      focus = false,
      select = false,
      autoFocus = false
    } = this.props;
    const {
      field
    } = this;
    if (multiline && field) autosize(field);
    if (focus || autoFocus) {
      autofocus(field);
    }
    if (select) field === null || field === void 0 || field.select();
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      multiline
    } = this.props;
    const {
      field
    } = this;
    const {
      value: nextValue,
      focus,
      select,
      countdown
    } = nextProps;
    if (multiline && field) autosize.update(field);
    if (focus) field === null || field === void 0 || field.focus();
    if (select) field === null || field === void 0 || field.select();
    if (countdown && nextValue && nextValue !== (field === null || field === void 0 ? void 0 : field.value)) {
      this.setState({
        countdown: String(nextValue).length
      });
    }
  }
  onBlurHandler(event) {
    const {
      onBlur
    } = this.props;
    this.setState({
      focused: false
    });
    if (onBlur) onBlur(event);
  }
  onFocusHandler(event) {
    const {
      onFocus
    } = this.props;
    this.setState({
      focused: true
    });
    if (onFocus) onFocus(event);
  }
  onInputHandler(event) {
    const {
      onInput
    } = this.props;
    const {
      target: {
        value
      }
    } = event;
    this.setState({
      countdown: value.length
    });
    if (typeof onInput === 'function') onInput(event);
  }
  onChangeHandler(event) {
    const {
      onChange
    } = this.props;
    if (typeof onChange === 'function') onChange(event);
  }
  onClearHandler() {
    const {
      onChange
    } = this.props;
    const {
      field,
      isUncontrolledValue,
      onInputHandler,
      onChangeHandler
    } = this;
    if (isUncontrolledValue) field.value = '';
    onInputHandler(emptyEvent);
    if (onChange) onChangeHandler(emptyEvent);
  }
  setField(field) {
    const {
      setField
    } = this.props;
    if (setField) setField(field);
    this.field = field;
  }
  setRef(ref) {
    const {
      innerRef
    } = this.props;
    if (typeof innerRef === 'function') return innerRef(ref);
    if (innerRef) innerRef.current = ref;
  }
  get helperConfig() {
    const {
      message,
      messageAction,
      messageActionOnClick,
      messageShow,
      helper
    } = this.props;
    return computeHelperProps$1({
      message,
      messageAction,
      messageActionOnClick,
      messageShow,
      helper
    });
  }
  renderChildren() {
    const {
      children,
      embeddedChildren = true
    } = this.props;
    if (embeddedChildren) {
      return /*#__PURE__*/_jsx("div", {
        className: "".concat(formControlNamespace, "__embedded")
      }, void 0, children);
    }
    return children;
  }
  renderlabel() {
    const {
      label,
      labelSrOnly
    } = this.props;
    if (labelSrOnly) {
      return /*#__PURE__*/_jsx(VisuallyHidden, {}, void 0, label);
    }
    return /*#__PURE__*/_jsx("span", {
      className: "".concat(formControlNamespace, "__label")
    }, void 0, label);
  }
  renderHelper() {
    const {
      modifier,
      numeric = false,
      id
    } = this.props;
    const {
      helper,
      show,
      action,
      onActionClick
    } = this.helperConfig;
    const iconError = (modifier === 'error' || modifier === 'warning') && helper && !numeric && (_ErrorIcon$1 || (_ErrorIcon$1 = /*#__PURE__*/_jsx(ErrorIcon$1, {
      namespace: formControlNamespace
    })));
    return /*#__PURE__*/React.createElement(React.Fragment, null, iconError, show && (helper || action) && /*#__PURE__*/_jsx("span", {
      id: "".concat(id, "-message"),
      className: "".concat(formControlNamespace, "__message")
    }, void 0, helper, onActionClick && action && /*#__PURE__*/_jsx("span", {
      onClick: onActionClick,
      className: "".concat(formControlNamespace, "__message-action")
    }, void 0, action)));
  }
  render() {
    const _this$props = this.props,
      {
        centered = false,
        children,
        className,
        clear = false,
        countdown = false,
        disabled = false,
        defaultValue,
        embeddedChildren,
        focus,
        id,
        innerRef,
        inputProps = {},
        label,
        labelSrOnly,
        maxLength = 120,
        message,
        messageAction,
        messageActionOnClick,
        messageShow,
        modifier,
        multiline = false,
        numeric = false,
        onChange,
        placeholder,
        prefix = '',
        rows = 1,
        select,
        setField,
        split = false,
        srLabel,
        suffix = '',
        value,
        autoFocus
      } = _this$props,
      otherProps = _objectWithoutProperties(_this$props, _excluded$1);
    const {
      focused,
      height,
      countdown: count
    } = this.state;
    const {
      helper,
      action
    } = this.helperConfig;
    const showBottomInfo = !numeric && !split && (countdown || helper || action);
    const classes = classNames(formControlNamespace, "".concat(formControlNamespace, "--textfield"), className, {
      ["".concat(formControlNamespace, "--multiline")]: multiline,
      ["".concat(formControlNamespace, "--countdown")]: countdown,
      ["".concat(formControlNamespace, "--centered")]: centered,
      ["".concat(formControlNamespace, "--focused")]: focused,
      ["".concat(formControlNamespace, "--").concat(modifier)]: modifier,
      ["".concat(formControlNamespace, "--disabled")]: disabled
    });
    const getAriaDescribedby = () => {
      const {
        'aria-describedby': ariaDescribedby
      } = inputProps;
      if (split) return ariaDescribedby;
      if ((helper || action) && countdown) return "".concat(id, "-message ").concat(id, "-count");
      if (countdown) return "".concat(id, "-count");
      if (helper || action) return "".concat(id, "-message");
    };
    const fieldProps = _objectSpread(_objectSpread(_objectSpread({}, otherProps), inputProps), {}, {
      id,
      defaultValue,
      value,
      className: classNames("".concat(formControlNamespace, "__field"), {
        ["".concat(formControlNamespace, "__field--multiline")]: multiline
      }),
      disabled: disabled || modifier === 'readonly',
      maxLength: maxLength,
      style: multiline ? {
        height
      } : {},
      placeholder,
      onBlur: this.onBlurHandler,
      onFocus: this.onFocusHandler,
      onChange: this.onChangeHandler,
      ref: this.setField,
      'aria-invalid': modifier === 'error' || modifier === 'warning' || (inputProps === null || inputProps === void 0 ? void 0 : inputProps['aria-invalid']) || otherProps['aria-invalid'],
      'aria-label': srLabel,
      'aria-describedby': getAriaDescribedby(),
      rows: rows,
      autoFocus: focus || autoFocus
    });
    const Input = multiline ? 'textarea' : 'input';
    return /*#__PURE__*/_jsx(AndesContext.Consumer, {}, void 0, andesContext => {
      const i18n = getPatchedI18n(andesContext, enTranslations, esTranslations, ptTranslations);
      return /*#__PURE__*/React.createElement("div", {
        className: classes,
        ref: this.setRef
      }, label && /*#__PURE__*/_jsx("label", {
        htmlFor: id
      }, void 0, this.renderlabel()), /*#__PURE__*/_jsx(FocusRing, {
        within: true,
        isTextInput: true,
        focusRingClass: focused ? 'visible-focused' : ''
      }, void 0, /*#__PURE__*/_jsx("div", {
        className: "".concat(formControlNamespace, "__control")
      }, void 0, prefix && /*#__PURE__*/_jsx("span", {
        className: "".concat(formControlNamespace, "__prefix")
      }, void 0, prefix), /*#__PURE__*/React.createElement(Input, _extends({
        key: "field"
      }, fieldProps, {
        onInput: this.onInputHandler
      })), suffix && /*#__PURE__*/_jsx("span", {
        className: "".concat(formControlNamespace, "__suffix")
      }, void 0, suffix), modifier === 'completed' && /*#__PURE__*/_jsx(ValidatedIcon$1, {
        namespace: formControlNamespace,
        notificationsLabel: i18n.pgettext(namespace, 'Campo de texto valido')
      }), modifier === 'indeterminate' && /*#__PURE__*/_jsx(ProgressIndicatorCircular, {
        className: "".concat(formControlNamespace, "--textfield-progress"),
        size: "xsmall",
        srAnnouncement: i18n.pgettext(namespace, 'Campo de texto procesando'),
        srLabel: i18n.pgettext(namespace, 'Campo de texto procesando')
      }), clear && /*#__PURE__*/_jsx(ClearButton$1, {
        "aria-label": i18n.pgettext(namespace, 'Borrar'),
        namespace: formControlNamespace,
        onClick: this.onClearHandler
      }), children && this.renderChildren())), showBottomInfo && /*#__PURE__*/_jsx("div", {
        className: "".concat(formControlNamespace, "__bottom")
      }, void 0, this.renderHelper(), countdown && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/_jsx("span", {
        id: "".concat(id, "-countdown-progress"),
        className: "".concat(formControlNamespace, "__countdown"),
        role: "progressbar",
        "aria-valuenow": count,
        "aria-valuemin": 0,
        "aria-valuemax": Number(maxLength),
        "aria-label": i18n.pgettext(namespace, 'Maximo {0} caracteres', maxLength)
      }, void 0, "".concat(count, " / ").concat(maxLength)), /*#__PURE__*/_jsx(VisuallyHidden, {
        id: "".concat(id, "-count"),
        "aria-live": "polite",
        "aria-atomic": "true"
      }, void 0, i18n.pgettext(namespace, '{0} caracteres restantes', Number(maxLength) - count)))));
    });
  }
}
TextField.displayName = 'TextField';
var TextField$1 = TextField;
var _ErrorIcon;
const _excluded = ["autoFocus", "children", "className", "defaultSelection", "defaultValue", "disabled", "helper", "inputProps", "id", "label", "list", "message", "messageShow", "modifier", "placeholder", "onBlur", "onChange", "onDropdownOpen", "onFocus", "splitIcon", "splitLabel", "splitPosition", "srLabel", "value"];
const SplitTextfield = /*#__PURE__*/function () {
  const SplitTextfield = /*#__PURE__*/forwardRef((props, ref) => {
    var _span;
    const {
        autoFocus = false,
        children,
        className,
        defaultSelection = 0,
        defaultValue,
        disabled = false,
        helper,
        inputProps,
        id,
        label,
        list,
        message,
        messageShow,
        modifier,
        placeholder,
        onBlur,
        onChange,
        onDropdownOpen,
        onFocus,
        splitIcon,
        splitLabel,
        splitPosition = 'left',
        srLabel,
        value
      } = props,
      other = _objectWithoutProperties(props, _excluded);
    const getDefaultValue = () => {
      var _list$content$default;
      return list === null || list === void 0 || (_list$content$default = list.content[defaultSelection]) === null || _list$content$default === void 0 ? void 0 : _list$content$default.title;
    };
    const ownContainerRef = useRef(null);
    const containerRef = ref || ownContainerRef;
    const fieldRef = useRef();
    const componentId = useId(id);
    const helperConfig = computeHelperProps$1({
      message,
      messageShow,
      helper
    });
    const {
      isFocusVisible,
      focusProps
    } = useFocusRing({
      isTextInput: true
    });
    const isReadonly = modifier === 'readonly';
    const containerClassNames = classNames(formControlNamespace, className, "".concat(formControlNamespace, "--textfield"), "".concat(formControlNamespace, "--split"), {
      ["".concat(formControlNamespace, "--").concat(modifier)]: modifier,
      ["".concat(formControlNamespace, "--focused")]: autoFocus,
      ["".concat(formControlNamespace, "--disabled")]: disabled
    });
    const splitControlClassNames = classNames("".concat(formControlNamespace, "__control"), {
      ["".concat(formControlNamespace, "__control--visible-focused")]: isFocusVisible
    });
    const splitButtonClassNames = classNames(splitButtonNamespace, {
      ["".concat(splitButtonNamespace, "--right")]: splitPosition === 'right',
      ["".concat(splitButtonNamespace, "--readonly")]: isReadonly
    });
    const fieldClassNames = classNames("".concat(formControlNamespace, "__field ").concat(formControlNamespace, "__split-field"), {
      ["".concat(formControlNamespace, "__split-field--readonly")]: isReadonly
    });
    const iconClassNames = classNames("".concat(splitButtonNamespace, "-content--icon"), {
      ["".concat(splitButtonNamespace, "-content--icon-disabled")]: disabled
    });
    const splitItemClassNames = "".concat(formControlNamespace, "--split-item");
    const customOnClick = item => {
      if (typeof (list === null || list === void 0 ? void 0 : list.onClick) === 'function') {
        list.onClick(item);
      }
    };
    const customOnBlur = event => {
      var _fieldRef$current;
      handlers.onBlur(containerRef.current, (_fieldRef$current = fieldRef.current) === null || _fieldRef$current === void 0 ? void 0 : _fieldRef$current.field.value);
      if (typeof focusProps.onBlur === 'function') focusProps.onBlur(event);
      if (typeof onBlur === 'function') onBlur(event);
    };
    const customOnFocus = event => {
      handlers.onFocus(containerRef.current);
      if (typeof focusProps.onFocus === 'function') focusProps.onFocus(event);
      if (typeof onFocus === 'function') onFocus(event);
    };
    const renderCustomTrigger = triggerContent => {
      let adaptedTriggerContent = triggerContent;
      if (splitLabel) adaptedTriggerContent = splitLabel;
      if (splitIcon) {
        adaptedTriggerContent = /*#__PURE__*/React.createElement(React.Fragment, null, _span || (_span = /*#__PURE__*/_jsx("span", {
          className: iconClassNames
        }, void 0, splitIcon)), adaptedTriggerContent);
      }
      return adaptedTriggerContent;
    };
    const fieldProps = _objectSpread(_objectSpread({}, other), {}, {
      inputProps: _objectSpread({
        'aria-describedby': helperConfig.show && helperConfig.helper ? "".concat(componentId, "-message") : undefined
      }, inputProps),
      'aria-invalid': modifier === 'error',
      'aria-labelledby': "".concat(componentId, "-dropdown-trigger"),
      srLabel: srLabel || label,
      className: fieldClassNames,
      children,
      defaultValue,
      disabled,
      id: componentId,
      modifier: modifier !== 'error' ? modifier : undefined,
      onBlur: customOnBlur,
      onChange,
      onFocus: customOnFocus,
      placeholder,
      ref: fieldRef,
      value
    });
    return /*#__PURE__*/React.createElement("div", {
      className: containerClassNames,
      ref: containerRef
    }, label && /*#__PURE__*/_jsx("label", {
      htmlFor: componentId
    }, void 0, /*#__PURE__*/_jsx("span", {
      className: "".concat(formControlNamespace, "__label")
    }, void 0, label)), /*#__PURE__*/_jsx("div", {
      className: splitControlClassNames
    }, void 0, splitPosition === 'right' && /*#__PURE__*/React.createElement(TextField$1, _extends({}, fieldProps, {
      split: true
    })), /*#__PURE__*/_jsx(DropdownStandalone, {
      className: splitButtonClassNames,
      label: srLabel || label,
      menuAlignment: "bottom",
      defaultValue: getDefaultValue(),
      onOpen: onDropdownOpen,
      disabled: disabled || isReadonly,
      id: "".concat(componentId, "-dropdown"),
      renderCustomTrigger: renderCustomTrigger
    }, void 0, list === null || list === void 0 ? void 0 : list.content.map(item => /*#__PURE__*/
    _jsx(DropdownItem, {
      value: item.title,
      className: splitItemClassNames,
      title: item.title,
      onSelect: () => customOnClick(item),
      leftContent: item.icon && /*#__PURE__*/_jsx(item.icon, {})
    }, transformToKebabCase(item.title)))), splitPosition === 'left' && /*#__PURE__*/React.createElement(TextField$1, _extends({}, fieldProps, {
      split: true
    }))), helperConfig.show && helperConfig.helper && !disabled && !isReadonly && /*#__PURE__*/_jsx("div", {
      className: "".concat(formControlNamespace, "__bottom")
    }, void 0, modifier === 'error' && (_ErrorIcon || (_ErrorIcon = /*#__PURE__*/_jsx(ErrorIcon$1, {
      namespace: formControlNamespace
    }))), /*#__PURE__*/_jsx("span", {
      className: "".concat(formControlNamespace, "__message"),
      id: "".concat(componentId, "-message")
    }, void 0, helperConfig.helper)));
  });
  SplitTextfield.displayName = 'SplitTextfield';
  return SplitTextfield;
}();
var SplitTextfield$1 = SplitTextfield;
const TextfieldContainer = /*#__PURE__*/function () {
  const TextfieldContainer = /*#__PURE__*/forwardRef((props, ref) => {
    const componentId = useId(props.id);
    return /*#__PURE__*/(
      React.createElement(TextField$1, _extends({
        innerRef: ref
      }, props, {
        id: componentId
      }))
    );
  });
  TextfieldContainer.displayName = 'TextfieldContainer';
  return TextfieldContainer;
}();
var TextfieldContainer$1 = TextfieldContainer;
export { CodeInput$1 as CodeInput, Numeric$1 as Numeric, PasswordContainer$1 as Password, SplitTextfield$1 as SplitTextfield, TextfieldContainer$1 as TextField, TextfieldContainer$1 as default };
